import axios from 'axios';
import notificationService from './notificationService';
import { isSessionEndCode, sessionEndMessage } from './sessionEnd';

// Centralized API client with automatic JWT token injection
class ApiClient {
  constructor() {
    this.client = axios.create({
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor - automatically add JWT token and org context
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add organization context header (defense-in-depth)
        const orgId = localStorage.getItem('orgId');
        if (orgId) {
          config.headers['X-Org-Id'] = orgId;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle common errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        // Canceled/aborted requests (component unmounted, navigation away) are
        // not failures — never toast or log them, just propagate for callers
        // that check axios.isCancel.
        if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') {
          return Promise.reject(error);
        }
        const requestUrl = error.config?.url || '';
        const errorData = error.response?.data || {};

        // Skip notifications for license-related endpoints and errors
        // License errors are handled by LicenseContext and LicenseBlockedPage
        const isLicenseError = errorData.error === 'LICENSE_INVALID';
        const isLicenseEndpoint = requestUrl.includes('/api/license/');

        if (isLicenseError || isLicenseEndpoint) {
          // Let license errors pass through silently - LicenseContext handles them
          return Promise.reject(error);
        }

        // Handle 401 Unauthorized or 403 Forbidden
        if (error.response?.status === 401 || error.response?.status === 403) {
          const errorMessage = errorData.message || '';

          // Only treat as session expiration if:
          // 1. Status is 401 (always authentication issue)
          // 2. Error message explicitly mentions session/token/authentication issues
          // 3. NOT from an endpoint that might not exist yet
          const isRumEndpoint = requestUrl.includes('/api/rum/') || requestUrl.includes('/api/v1.0/rum/');
          const isValidationEndpoint = requestUrl.includes('/dashboard-query-validation/') || requestUrl.includes('/query-validation/');
          const isOptionalEndpoint = requestUrl.includes('/query/test') ||
                                     requestUrl.includes('/sla') ||
                                     requestUrl.includes('/metrics/query') ||
                                     requestUrl.includes('/metrics/labels');
          // Only trust genuine JWT-bad signals. Spring Security's
          // default 401 ("Full authentication is required") contains
          // "authentication" but also fires for transient 401s that
          // shouldn't log the user out.
          const isSessionExpired = error.response?.status === 401 &&
                                  (isSessionEndCode(errorData) ||
                                   errorMessage.toLowerCase().includes('expired') ||
                                   errorMessage.toLowerCase().includes('invalid token'));

          // Don't log out for errors on optional/RUM/validation endpoints (backend might not be ready)
          if (isSessionExpired && !isRumEndpoint && !isValidationEndpoint && !isOptionalEndpoint) {
            // Clear auth token
            localStorage.removeItem('authToken');

            // Show session expired message
            notificationService.warning(sessionEndMessage(errorData, 'Your session has expired. Please login again.'), {
              autoHideDuration: 5000,
            });

            // Redirect to root path after a short delay (ProtectedRoute will handle showing login)
            setTimeout(() => {
              window.location.href = '/';
            }, 1000);
          } else if (error.response?.status === 403) {
            // This is a permissions issue, not session expiration
            // Only show error if it's not a RUM/validation endpoint (which might not exist yet)
            if (!isRumEndpoint && !isValidationEndpoint) {
              notificationService.error('Access denied. You do not have permission to perform this action.');
            }
          }
        }

        // Handle datasource unavailable (503)
        if (error.response?.status === 503) {
          const msg = error.response?.data?.message || 'Datasource is unavailable';
          notificationService.error(msg, { autoHideDuration: 5000 });
        }
        // Handle 500 Server Error - but not for RUM endpoints that might not exist
        else if (error.response?.status >= 500) {
          const isRumEndpoint = requestUrl.includes('/api/rum/') || requestUrl.includes('/api/v1.0/rum/');

          if (!isRumEndpoint) {
            notificationService.error('Server error. Please try again later.');
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // HTTP methods with automatic token injection
  get(url, config = {}) {
    return this.client.get(url, config);
  }

  post(url, data = {}, config = {}) {
    return this.client.post(url, data, config);
  }

  put(url, data = {}, config = {}) {
    return this.client.put(url, data, config);
  }

  delete(url, config = {}) {
    return this.client.delete(url, config);
  }

  patch(url, data = {}, config = {}) {
    return this.client.patch(url, data, config);
  }

  // Create a new instance with custom base URL
  createInstance(baseURL, customConfig = {}) {
    const instance = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
      ...customConfig,
    });

    // Apply the same interceptors to the new instance
    instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        const orgId = localStorage.getItem('orgId');
        if (orgId) {
          config.headers['X-Org-Id'] = orgId;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        // Canceled/aborted requests (component unmounted, navigation away) are
        // not failures — never toast or log them, just propagate for callers
        // that check axios.isCancel.
        if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') {
          return Promise.reject(error);
        }
        const requestUrl = error.config?.url || '';
        const errorData = error.response?.data || {};

        // Skip notifications for license-related endpoints and errors
        // License errors are handled by LicenseContext and LicenseBlockedPage
        const isLicenseError = errorData.error === 'LICENSE_INVALID';
        const isLicenseEndpoint = requestUrl.includes('/api/license/');

        if (isLicenseError || isLicenseEndpoint) {
          // Let license errors pass through silently - LicenseContext handles them
          return Promise.reject(error);
        }

        // Handle 401 Unauthorized or 403 Forbidden
        if (error.response?.status === 401 || error.response?.status === 403) {
          const errorMessage = errorData.message || '';

          // Only treat as session expiration if:
          // 1. Status is 401 (always authentication issue)
          // 2. Error message explicitly mentions session/token/authentication issues
          // 3. NOT from an endpoint that might not exist yet
          const isRumEndpoint = requestUrl.includes('/api/rum/') || requestUrl.includes('/api/v1.0/rum/');
          const isValidationEndpoint = requestUrl.includes('/dashboard-query-validation/') || requestUrl.includes('/query-validation/');
          const isOptionalEndpoint = requestUrl.includes('/query/test') ||
                                     requestUrl.includes('/sla') ||
                                     requestUrl.includes('/metrics/query') ||
                                     requestUrl.includes('/metrics/labels');
          // Only trust genuine JWT-bad signals. Spring Security's
          // default 401 ("Full authentication is required") contains
          // "authentication" but also fires for transient 401s that
          // shouldn't log the user out.
          const isSessionExpired = error.response?.status === 401 &&
                                  (isSessionEndCode(errorData) ||
                                   errorMessage.toLowerCase().includes('expired') ||
                                   errorMessage.toLowerCase().includes('invalid token'));

          // Don't log out for errors on optional/RUM/validation endpoints (backend might not be ready)
          if (isSessionExpired && !isRumEndpoint && !isValidationEndpoint && !isOptionalEndpoint) {
            // Clear auth token
            localStorage.removeItem('authToken');

            // Show session expired message
            notificationService.warning(sessionEndMessage(errorData, 'Your session has expired. Please login again.'), {
              autoHideDuration: 5000,
            });

            // Redirect to root path after a short delay (ProtectedRoute will handle showing login)
            setTimeout(() => {
              window.location.href = '/';
            }, 1000);
          } else if (error.response?.status === 403) {
            // This is a permissions issue, not session expiration
            // Only show error if it's not a RUM/validation endpoint (which might not exist yet)
            if (!isRumEndpoint && !isValidationEndpoint) {
              notificationService.error('Access denied. You do not have permission to perform this action.');
            }
          }
        }

        // Handle datasource unavailable (503)
        if (error.response?.status === 503) {
          const msg = error.response?.data?.message || 'Datasource is unavailable';
          notificationService.error(msg, { autoHideDuration: 5000 });
        }
        // Handle 500 Server Error - but not for RUM endpoints that might not exist
        else if (error.response?.status >= 500) {
          const isRumEndpoint = requestUrl.includes('/api/rum/') || requestUrl.includes('/api/v1.0/rum/');

          if (!isRumEndpoint) {
            notificationService.error('Server error. Please try again later.');
          }
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }

  // Helper method to check if user is authenticated
  isAuthenticated() {
    const token = localStorage.getItem('authToken');
    return !!token;
  }

  // Helper method to get auth headers manually
  getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  // Method to set auth token (useful for login)
  setAuthToken(token) {
    localStorage.setItem('authToken', token);
  }

  // Method to clear auth token (useful for logout)
  clearAuthToken() {
    localStorage.removeItem('authToken');
  }
}

// Create and export a singleton instance
const apiClient = new ApiClient();

export default apiClient;

// Export the class for creating custom instances
export { ApiClient };